import { store } from '@/store';
import constants from '@/utilities/constants';
import { ResourceType } from '@/types/resources';

const getRecents = (type, iconGetter, projectGetter, additionalIconGetter, count = 2) => {
  return store.getters.getElectronRecentlyViewed
    .map(({ label, id, icon, additionalIcon, type: resourceType, project_id }) => {
      if (resourceType === type) {
        const project_id_value = project_id ?? projectGetter(id);
        return {
          title: label,
          id,
          icon: iconGetter(icon),
          ...(additionalIcon &&
            additionalIconGetter && { additionalIcon: additionalIconGetter(additionalIcon) }),
          ...(project_id_value && { project_id: project_id_value }),
        };
      }
      return null;
    })
    .filter(Boolean)
    .splice(0, count);
};

const getFavourites = (type, iconGetter, projectGetter) => {
  return store.getters.getFavourites
    .map(({ resource_id, resource_type, title, project_id }) => {
      if (resource_type === type) {
        const project_id_value = project_id ?? projectGetter(resource_id);
        return {
          title,
          id: resource_id,
          icon: iconGetter(resource_id),
          ...(project_id_value && { project_id: project_id_value }),
        };
      }
      return null;
    })
    .filter(Boolean)
    .splice(0, 2);
};

const getDefaultItems = (type, recents, favourites, items) => {
  let defaultItems = recents;
  if (defaultItems.length < 2) {
    defaultItems = [...recents, ...favourites].filter(Boolean).splice(0, 2);
  }
  if (defaultItems.length < 2) {
    const sortedItems = Object.values(items).sort(sortItems).splice(0, 2);

    defaultItems = [...defaultItems, ...sortedItems].splice(0, 2);
  }
  return defaultItems;
};

const sortItems = (a, b) => {
  return b.time_created - a.time_created;
};

const getDefaultCards = () => {
  const recents = getRecents(
    constants.routeNames.boardCard,
    (icon) => icon,
    (id) => null
  );

  const cards = store.getters.getCards;

  const favourites = getFavourites(
    ResourceType.Card,
    (resource_id) => cards?.[resource_id]?.icon,
    (id) => null
  );

  return getDefaultItems(constants.routeNames.boardCard, recents, favourites, cards);
};

const getDefaultEpics = () => {
  const recents = getRecents(
    constants.epicRoute.name,
    (icon) => icon,
    (id) => null,
    (additionalIcon) => additionalIcon
  );

  const epics = store.getters.getEpics;

  return getDefaultItems(constants.epicRoute.name, recents, [], epics);
};

const getDefaultPages = (amountToRetrieve = 2) => {
  const pages = store.getters.getPages;

  const recents = getRecents(
    constants.routeNames.page,
    (icon) => ({ src: icon.name, color: icon.color, type: icon.type }),
    (id) => pages?.[id]?.project_id,
    null,
    amountToRetrieve
  );

  const favourites = getFavourites(
    ResourceType.Page,
    (resource_id) => pages?.[resource_id]?.icon,
    (resource_id) => pages?.[resource_id]?.project_id
  );
  return getDefaultItems(constants.routeNames.page, recents, favourites, pages);
};

const getDefaultBoards = () => {
  const boards = store.getters.getFlatBoards;

  const recents = getRecents(
    constants.routeNames.board,
    (icon) => ({ src: icon.name, color: icon.color, type: icon.type }),
    (id) => boards?.[id]?.project_id
  );

  const favourites = getFavourites(
    ResourceType.Board,
    (resource_id) => boards?.[resource_id]?.icon,
    (resource_id) => boards?.[resource_id]?.project_id
  );

  return getDefaultItems(constants.routeNames.board, recents, favourites, boards);
};

const getDefaultProjects = () => {
  const recents = getRecents(
    constants.routeNames.space,
    (icon) => ({ src: icon.name, color: icon.color, type: icon.type }),
    (id) => store.getters.getProjects[id]?.project_id
  );

  const usersProjects = Object.values(store.getters.getProjects).sort(sortItems).splice(0, 2);

  const allProjects = Object.values(store.getters.getTeamProjects).sort(sortItems).splice(0, 2);

  return getDefaultItems(constants.routeNames.space, recents, usersProjects, allProjects);
};

export {
  getDefaultCards,
  getDefaultEpics,
  getDefaultPages,
  getDefaultBoards,
  getDefaultProjects,
  getRecents,
  getFavourites,
};
